// 7 curated premium wedding invitation themes for Wedifylab.
// Each theme is rendered by InvitationPremium.jsx with a mobile-centered
// layout (max-width 430px, centered on desktop) — themes differ in palette,
// typography, decoration style, hero variant, and section ornaments.

// Themes accessible without Paket Premium (standard tier fallback).
export const STANDARD_THEME_IDS = new Set(["editorial-bw"]);
export const isStandardTheme = (id) => STANDARD_THEME_IDS.has(id);

export const PREMIUM_THEMES = [
  {
    id: "editorial-bw",
    name: "Editorial Monokrom",
    cat: "Editorial B&W",
    mode: "light",
    hero: "editorial-overlay",   // Full-width photo + serif overlay
    decor: "minimal-line",       // Thin single-line dividers
    palette: {
      bg: "#f7f6f4", cream: "#141414", accent: "#141414", accent2: "#4a4a4a",
      secondary: "#eaeaea", dim: "rgba(20,20,20,0.62)",
    },
    fonts: {
      script: "'Cormorant Garamond', serif",
      serif: "'Cormorant Garamond', serif",
      sans: "'Inter', sans-serif",
    },
    googleFonts: "Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Inter:wght@400;500;600",
    ornament: "diamond",
  },
  {
    id: "ar-rum-modern",
    name: "Modern Ar-Rum 21",
    cat: "Modern Colorful",
    mode: "dark",
    hero: "verse-photo",         // Q.S. Ar Rum: 21 above hero photo
    decor: "arch-frame",
    palette: {
      bg: "#0f1210", cream: "#f6efe1", accent: "#c9a35c", accent2: "#e6c98a",
      secondary: "#1a2620", dim: "rgba(246,239,225,0.75)",
    },
    fonts: {
      script: "'Cormorant Garamond', serif",
      serif: "'Cormorant Garamond', serif",
      sans: "'Inter', sans-serif",
    },
    googleFonts: "Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Inter:wght@400;500;600",
    ornament: "leaf",
  },
  {
    id: "vintage-floral",
    name: "Romantis Vintage Floral",
    cat: "Mauve & Rose",
    mode: "light",
    hero: "floral-frame",        // Oval photo inside floral border
    decor: "vintage-floral",
    palette: {
      bg: "#f7ecec", cream: "#4a1f28", accent: "#a86477", accent2: "#c98599",
      secondary: "#efd8d8", dim: "rgba(74,31,40,0.68)",
    },
    fonts: {
      script: "'Great Vibes', cursive",
      serif: "'Cormorant Garamond', serif",
      sans: "'Cormorant Garamond', serif",
    },
    googleFonts: "Great+Vibes&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400",
    ornament: "floral",
  },
  {
    id: "dark-luxury",
    name: "Dark Luxury Art Deco",
    cat: "Black & Gold",
    mode: "dark",
    hero: "art-deco",            // Art deco framed hero
    decor: "art-deco",
    palette: {
      bg: "#08060b", cream: "#f5e8b6", accent: "#d4af37", accent2: "#f4d76e",
      secondary: "#1a1108", dim: "rgba(245,232,182,0.72)",
    },
    fonts: {
      script: "'Great Vibes', cursive",
      serif: "'Playfair Display', serif",
      sans: "'Playfair Display', serif",
    },
    googleFonts: "Great+Vibes&family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400",
    ornament: "diamond",
  },
  {
    id: "pastel-watercolor",
    name: "Pastel Watercolor",
    cat: "Blush & Lavender",
    mode: "light",
    hero: "pastel-wash",         // Watercolor gradient wash
    decor: "watercolor",
    palette: {
      bg: "#fbf6fa", cream: "#3a2f4a", accent: "#c8a8d4", accent2: "#f4c7d0",
      secondary: "#f0e3ec", dim: "rgba(58,47,74,0.62)",
    },
    fonts: {
      script: "'Allura', cursive",
      serif: "'Cormorant Garamond', serif",
      sans: "'Cormorant Garamond', serif",
    },
    googleFonts: "Allura&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400",
    ornament: "floral",
  },
  {
    id: "botanical-green",
    name: "Botanical Sage",
    cat: "Sage & Earth",
    mode: "light",
    hero: "botanical-leaf",      // Leaf-decorated hero
    decor: "botanical",
    palette: {
      bg: "#f2ede1", cream: "#26332a", accent: "#5f7358", accent2: "#8ea583",
      secondary: "#e0dcc5", dim: "rgba(38,51,42,0.68)",
    },
    fonts: {
      script: "'Pinyon Script', cursive",
      serif: "'Cormorant Garamond', serif",
      sans: "'Cormorant Garamond', serif",
    },
    googleFonts: "Pinyon+Script&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400",
    ornament: "leaf",
  },
  {
    id: "minimal-contemporary",
    name: "Minimalis Kontemporer",
    cat: "White & Ink",
    mode: "light",
    hero: "geometric-frame",     // Simple geometric photo frame
    decor: "geometric",
    palette: {
      bg: "#ffffff", cream: "#0a0a0a", accent: "#0a0a0a", accent2: "#666666",
      secondary: "#f2f2f2", dim: "rgba(10,10,10,0.6)",
    },
    fonts: {
      script: "'Inter', sans-serif",
      serif: "'Inter', sans-serif",
      sans: "'Inter', sans-serif",
    },
    googleFonts: "Inter:wght@300;400;500;600;700",
    ornament: "diamond",
  },
];

export function getPremiumTheme(id) {
  return PREMIUM_THEMES.find((t) => t.id === id) || PREMIUM_THEMES[0];
}

// ---------------------------------------------------------------
// Decorative SVG helpers — pure inline, per-theme decor variant.
// ---------------------------------------------------------------

// A section divider that respects the theme's decor style.
export function DecorDivider({ theme, w = 260 }) {
  const acc = theme.palette.accent;
  const acc2 = theme.palette.accent2;
  switch (theme.decor) {
    case "minimal-line":
      return (
        <svg viewBox="0 0 260 20" width={w} className="mx-auto my-6 opacity-70">
          <line x1="0" y1="10" x2="260" y2="10" stroke={acc} strokeWidth="0.5" />
        </svg>
      );
    case "arch-frame":
      return (
        <svg viewBox="0 0 260 40" width={w} className="mx-auto my-6">
          <line x1="0" y1="20" x2="100" y2="20" stroke={acc} strokeWidth="0.8" />
          <line x1="160" y1="20" x2="260" y2="20" stroke={acc} strokeWidth="0.8" />
          <path d="M 108 22 Q 130 4 152 22" fill="none" stroke={acc} strokeWidth="1.2" />
          <circle cx="130" cy="22" r="2" fill={acc} />
        </svg>
      );
    case "vintage-floral":
      return (
        <svg viewBox="0 0 260 40" width={w} className="mx-auto my-6">
          <line x1="0" y1="20" x2="90" y2="20" stroke={acc} strokeWidth="0.5" />
          <line x1="170" y1="20" x2="260" y2="20" stroke={acc} strokeWidth="0.5" />
          <g transform="translate(130 20)">
            <path d="M -8 0 Q -4 -8 0 -6 Q 4 -8 8 0 Q 4 8 0 6 Q -4 8 -8 0 Z" fill={acc} opacity="0.55" />
            <circle cx="-14" cy="-6" r="2" fill={acc2} />
            <circle cx="14" cy="-6" r="2" fill={acc2} />
            <circle cx="-14" cy="6" r="2" fill={acc2} />
            <circle cx="14" cy="6" r="2" fill={acc2} />
            <path d="M -20 0 Q -18 -3 -14 -6" fill="none" stroke={acc} strokeWidth="0.5" />
            <path d="M 20 0 Q 18 -3 14 -6" fill="none" stroke={acc} strokeWidth="0.5" />
          </g>
        </svg>
      );
    case "art-deco":
      return (
        <svg viewBox="0 0 260 40" width={w} className="mx-auto my-6">
          <line x1="0" y1="20" x2="80" y2="20" stroke={acc} strokeWidth="0.8" />
          <line x1="180" y1="20" x2="260" y2="20" stroke={acc} strokeWidth="0.8" />
          <g transform="translate(130 20)">
            <path d="M -22 0 L 0 -14 L 22 0 L 0 14 Z" fill="none" stroke={acc} strokeWidth="1" />
            <path d="M -14 0 L 0 -8 L 14 0 L 0 8 Z" fill={acc} opacity="0.7" />
            <line x1="-30" y1="0" x2="-22" y2="0" stroke={acc} strokeWidth="0.8" />
            <line x1="22" y1="0" x2="30" y2="0" stroke={acc} strokeWidth="0.8" />
          </g>
        </svg>
      );
    case "watercolor":
      return (
        <svg viewBox="0 0 260 30" width={w} className="mx-auto my-6">
          <defs>
            <linearGradient id="wc" x1="0%" x2="100%">
              <stop offset="0%" stopColor={acc} stopOpacity="0" />
              <stop offset="50%" stopColor={acc} stopOpacity="0.6" />
              <stop offset="100%" stopColor={acc2} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M 10 15 Q 130 5 250 15" stroke="url(#wc)" strokeWidth="1.5" fill="none" />
          <circle cx="130" cy="15" r="3" fill={acc2} opacity="0.7" />
        </svg>
      );
    case "botanical":
      return (
        <svg viewBox="0 0 260 40" width={w} className="mx-auto my-6">
          <line x1="0" y1="20" x2="80" y2="20" stroke={acc} strokeWidth="0.6" />
          <line x1="180" y1="20" x2="260" y2="20" stroke={acc} strokeWidth="0.6" />
          <g transform="translate(130 20)">
            <path d="M 0 -14 Q -10 -8 -6 4" fill={acc} opacity="0.65" />
            <path d="M 0 -14 Q 10 -8 6 4" fill={acc} opacity="0.65" />
            <path d="M 0 -14 Q -3 0 -8 8" fill={acc} opacity="0.5" />
            <path d="M 0 -14 Q 3 0 8 8" fill={acc} opacity="0.5" />
            <line x1="0" y1="-14" x2="0" y2="12" stroke={acc} strokeWidth="0.6" />
          </g>
        </svg>
      );
    case "geometric":
    default:
      return (
        <svg viewBox="0 0 260 20" width={w} className="mx-auto my-6">
          <line x1="0" y1="10" x2="120" y2="10" stroke={acc} strokeWidth="0.5" />
          <line x1="140" y1="10" x2="260" y2="10" stroke={acc} strokeWidth="0.5" />
          <rect x="126" y="6" width="8" height="8" transform="rotate(45 130 10)" stroke={acc} strokeWidth="0.6" fill="none" />
        </svg>
      );
  }
}

// Corner ornaments (used by hero + section frames).
export function CornerOrnament({ theme, corner = "tl" }) {
  const acc = theme.palette.accent;
  const rot = { tl: 0, tr: 90, br: 180, bl: 270 }[corner] || 0;
  if (theme.decor === "art-deco") {
    return (
      <svg viewBox="0 0 60 60" width="52" height="52" style={{ position: "absolute", transform: `rotate(${rot}deg)` }} className={cornerStyle(corner)}>
        <path d="M 4 4 L 30 4 L 30 8 L 8 8 L 8 30 L 4 30 Z" fill={acc} opacity="0.8" />
        <path d="M 12 12 L 22 12 L 22 14 L 14 14 L 14 22 L 12 22 Z" fill={acc} opacity="0.5" />
      </svg>
    );
  }
  if (theme.decor === "vintage-floral") {
    return (
      <svg viewBox="0 0 80 80" width="70" height="70" style={{ position: "absolute", transform: `rotate(${rot}deg)`, opacity: 0.55 }} className={cornerStyle(corner)}>
        <g stroke={acc} strokeWidth="0.5" fill={acc}>
          <path d="M 8 8 Q 20 4 30 12 Q 22 22 12 20 Q 14 12 8 8 Z" fillOpacity="0.35" />
          <circle cx="24" cy="8" r="2" fillOpacity="0.6" />
          <circle cx="8" cy="24" r="2" fillOpacity="0.6" />
          <path d="M 30 12 Q 36 20 40 30" fill="none" strokeWidth="0.6" />
        </g>
      </svg>
    );
  }
  if (theme.decor === "botanical") {
    return (
      <svg viewBox="0 0 80 80" width="70" height="70" style={{ position: "absolute", transform: `rotate(${rot}deg)`, opacity: 0.7 }} className={cornerStyle(corner)}>
        <g fill={acc} stroke={acc} strokeWidth="0.6">
          <path d="M 4 4 Q 26 6 30 30 Q 12 30 6 20 Q 12 14 4 4 Z" fillOpacity="0.35" />
          <path d="M 12 4 Q 14 12 22 16" fill="none" />
          <path d="M 4 12 Q 12 14 16 22" fill="none" />
        </g>
      </svg>
    );
  }
  return null;
}

function cornerStyle(c) {
  switch (c) {
    case "tl": return "top-3 left-3";
    case "tr": return "top-3 right-3";
    case "br": return "bottom-3 right-3";
    case "bl": return "bottom-3 left-3";
    default: return "";
  }
}

// Backwards-compat: some code imports `ornamentPath`.
export function ornamentPath(_kind, accent) {
  return (
    <g stroke={accent} strokeWidth="1" fill="none">
      <path d="M10 20 H90" />
      <path d="M130 20 H210" />
      <circle cx="110" cy="20" r="4" fill={accent} fillOpacity="0.6" />
    </g>
  );
}
